import { RenameDialog } from "./RenameDialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  PencilIcon,
  Trash2Icon,
  CheckIcon,
  ChevronDownIcon,
  HistoryIcon,
  EllipsisIcon,
  MessageSquareIcon,
  SendIcon,
  Undo2Icon,
} from "lucide-react";
import { useState } from "react";

import { useAccess } from "~/access/AccessProvider";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "~/components/ui/menu";
import { toastManager } from "~/components/ui/toast";

import {
  changeDashboardArtifact,
  deleteDashboardArtifact,
  restoreDashboardArtifact,
  dashboardArtifactVersionsQueryOptions,
  dashboardArtifactsQueryKeys,
  type DashboardArtifactChange,
} from "./artifactsReactQuery";
import type { DashboardArtifactSummary, DashboardArtifactVersionSummary } from "./artifactTypes";

export const DASHBOARDS_ARTIFACTS_PUBLISH_PERMISSION = "tool.dashboards.artifacts.publish";

const VERSION_DATE = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

/** Owners and publishers may change a dashboard that is not archived. Mirrors the control plane. */
export function canEditArtifact(input: {
  artifact: Pick<DashboardArtifactSummary, "ownerEmail" | "status">;
  canPublish: boolean;
  viewerEmail: string | null;
}): boolean {
  if (input.artifact.status === "archived") return false;
  const isOwner =
    input.viewerEmail !== null &&
    input.viewerEmail.toLowerCase() === input.artifact.ownerEmail.toLowerCase();
  return isOwner || input.canPublish;
}

/** What the viewer may do with the version on screen. Mirrors the control plane's checks. */
export function artifactActionsFor(input: {
  artifact: Pick<DashboardArtifactSummary, "ownerEmail" | "publishedVersion" | "status">;
  canPublish: boolean;
  version: Pick<DashboardArtifactVersionSummary, "published">;
  versionCount: number;
  viewerEmail: string | null;
}): { delete: boolean; publish: "share" | "replace" | null; rollback: boolean } {
  const { artifact, canPublish, version } = input;
  if (artifact.status === "archived") return { delete: false, publish: null, rollback: false };
  return {
    delete: canEditArtifact(input),
    publish:
      canPublish && !version.published
        ? artifact.publishedVersion === null
          ? "share"
          : "replace"
        : null,
    rollback: canPublish && version.published && input.versionCount > 1,
  };
}

/** Publish or roll back, with toasts and a refresh of every artifact query. */
export function useArtifactChange(
  artifact: Pick<DashboardArtifactSummary, "id" | "title">,
  callbacks: { onChanged?: () => void } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { change: DashboardArtifactChange; versionId?: string }) =>
      changeDashboardArtifact(artifact.id, input.change, input.versionId),
    onError: (error) =>
      toastManager.add({
        description: error instanceof Error ? error.message : String(error),
        title: "The dashboard did not change",
        type: "error",
      }),
    onSuccess: async (_artifact, input) => {
      await queryClient.invalidateQueries({ queryKey: dashboardArtifactsQueryKeys.all });
      toastManager.add({
        description:
          input.change === "publish"
            ? "Everyone with Dashboards access now sees this version."
            : "Viewers see the previously published version again.",
        title: input.change === "publish" ? "Published" : "Rolled back",
        type: "success",
      });
      callbacks.onChanged?.();
    },
  });
}

/** Brings a deleted dashboard back and refreshes every artifact query. */
export function useArtifactRestore(
  artifact: Pick<DashboardArtifactSummary, "id" | "title">,
  onRestored?: () => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => restoreDashboardArtifact(artifact.id),
    onError: (error) =>
      toastManager.add({
        description: error instanceof Error ? error.message : String(error),
        title: "The dashboard was not restored",
        type: "error",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: dashboardArtifactsQueryKeys.all });
      toastManager.add({ title: `${artifact.title} restored`, type: "success" });
      onRestored?.();
    },
  });
}

/**
 * Deletes a dashboard at once, with no confirmation: the toast's Undo brings it
 * back, and "Recently deleted" in the catalog does later.
 */
export function useArtifactDelete(
  artifact: Pick<DashboardArtifactSummary, "id" | "slug" | "title">,
  onDeleted: () => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteDashboardArtifact(artifact.id),
    onError: (error) =>
      toastManager.add({
        description: error instanceof Error ? error.message : String(error),
        title: "The dashboard was not deleted",
        type: "error",
      }),
    onSuccess: async () => {
      toastManager.add({
        actionProps: {
          children: "Undo",
          onClick: () => {
            void restoreDashboardArtifact(artifact.id)
              .then(() =>
                queryClient.invalidateQueries({ queryKey: dashboardArtifactsQueryKeys.all }),
              )
              .then(() =>
                toastManager.add({ title: `${artifact.title} restored`, type: "success" }),
              )
              .catch(() =>
                toastManager.add({ title: "The dashboard was not restored", type: "error" }),
              );
          },
        },
        timeout: 10_000,
        title: `${artifact.title} deleted`,
        type: "success",
      });
      onDeleted();
      await queryClient.invalidateQueries({ queryKey: dashboardArtifactsQueryKeys.all });
    },
  });
}

/**
 * Publish, version history, and an overflow menu with Delete and with the
 * conversation that built the dashboard, when there was one.
 * `onSelectVersion(undefined)` shows what viewers see.
 */
export function ArtifactActions(props: {
  artifact: DashboardArtifactSummary;
  onDeleted: () => void;
  /** Present when the viewer may rename the dashboard in place. */
  onRename?: ((title: string) => void) | undefined;
  onSelectVersion: (version: number | undefined) => void;
  version: DashboardArtifactVersionSummary;
  viewerEmail: string | null;
}) {
  const { artifact, version } = props;
  const access = useAccess();
  const versions = useQuery(dashboardArtifactVersionsQueryOptions(artifact.id));
  const [renaming, setRenaming] = useState(false);
  const [confirming, setConfirming] = useState<"rollback" | null>(null);
  const allowed = artifactActionsFor({
    artifact,
    canPublish: access.can(DASHBOARDS_ARTIFACTS_PUBLISH_PERMISSION),
    version,
    versionCount: versions.data?.length ?? 0,
    viewerEmail: props.viewerEmail,
  });
  const change = useArtifactChange(artifact, {
    onChanged: () => props.onSelectVersion(undefined),
  });
  const remove = useArtifactDelete(artifact, props.onDeleted);
  const navigate = useNavigate();
  const conversationId = version.sourceSessionId ?? artifact.sourceSessionId;

  return (
    <>
      <Menu>
        <MenuTrigger render={<Button size="sm" variant="outline" />}>
          <HistoryIcon aria-hidden />
          Version {version.version}
          <ChevronDownIcon aria-hidden />
        </MenuTrigger>
        <MenuPopup align="end" className="min-w-56">
          {(versions.data ?? []).map((entry) => (
            <MenuItem
              key={entry.id}
              onClick={() => props.onSelectVersion(entry.published ? undefined : entry.version)}
            >
              {entry.id === version.id ? (
                <CheckIcon className="size-4" aria-hidden />
              ) : (
                <span className="size-4" aria-hidden />
              )}
              <span className="flex-1">
                Version {entry.version} · {VERSION_DATE.format(new Date(entry.createdAt))}
              </span>
              {entry.published ? (
                <span className="text-xs text-muted-foreground">Published</span>
              ) : null}
            </MenuItem>
          ))}
        </MenuPopup>
      </Menu>
      {allowed.publish ? (
        <Button
          size="sm"
          disabled={change.isPending}
          onClick={() => change.mutate({ change: "publish", versionId: version.id })}
        >
          <SendIcon aria-hidden />
          {allowed.publish === "share" ? "Share with team" : "Publish this version"}
        </Button>
      ) : null}
      {allowed.rollback ? (
        <Button
          size="sm"
          variant="outline"
          disabled={change.isPending}
          onClick={() => setConfirming("rollback")}
        >
          <Undo2Icon aria-hidden />
          Roll back
        </Button>
      ) : null}
      {conversationId || allowed.delete || props.onRename ? (
        <Menu>
          <MenuTrigger render={<Button aria-label="More actions" size="icon-sm" variant="ghost" />}>
            <EllipsisIcon aria-hidden />
          </MenuTrigger>
          <MenuPopup align="end" className="min-w-56">
            {conversationId ? (
              <MenuItem
                onClick={() =>
                  void navigate({
                    to: "/operators/$threadId",
                    params: { threadId: conversationId },
                  })
                }
              >
                <MessageSquareIcon aria-hidden />
                Open the original conversation
              </MenuItem>
            ) : null}
            {props.onRename ? (
              <MenuItem onClick={() => setRenaming(true)}>
                <PencilIcon aria-hidden />
                Rename dashboard
              </MenuItem>
            ) : null}
            {allowed.delete ? (
              <MenuItem
                variant="destructive"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                <Trash2Icon aria-hidden />
                Delete dashboard
              </MenuItem>
            ) : null}
          </MenuPopup>
        </Menu>
      ) : null}
      {props.onRename ? (
        <RenameDialog
          currentName={artifact.title}
          heading="Rename this dashboard"
          inputLabel="Dashboard title"
          onOpenChange={setRenaming}
          onRename={props.onRename}
          open={renaming}
        />
      ) : null}
      <AlertDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Roll back?</AlertDialogTitle>
            <AlertDialogDescription>
              Viewers go back to the version that was published before this one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>Cancel</AlertDialogClose>
            <AlertDialogClose
              render={<Button />}
              onClick={() => change.mutate({ change: "rollback" })}
            >
              Roll back
            </AlertDialogClose>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}
